import React from 'react';

import { Draggable } from '@mint-lib/icons';
import { cn } from '@mint-lib/tw-utils';

import { Checkbox } from '../../lib/checkbox.jsx';
import { TruncateText } from '../../lib/truncate-text.jsx';
import { typographyVariants } from '../../lib/typography.jsx';
import { itemVariants } from '../../lib/variants.jsx';

export interface DraggableItemProps {
  onSelect?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onItemClick?: (...args: any) => void;
  adornment?: React.ReactNode;
  adornmentPosition?: 'left' | 'right';
  draggableIconPosition?: 'left' | 'right' | 'none';
  title: string;
  description?: string;
  isDragged?: boolean;
  disabled?: boolean;
  checkbox?: boolean;
  checkboxProps?: React.ComponentProps<typeof Checkbox>;
  draggableIconProps?: React.ComponentProps<typeof Draggable>;
}

const DraggableItem = ({
  onSelect,
  onItemClick,
  adornmentPosition = 'left',
  draggableIconPosition = 'left',
  adornment,
  title,
  description,
  isDragged,
  disabled = false,
  checkbox,
  className,
  checkboxProps,
  draggableIconProps,
  ...props
}: React.ComponentProps<'button'> & DraggableItemProps) => {
  return (
    <button
      type="button"
      data-dragged={isDragged}
      data-size={description ? 'medium' : 'small'}
      disabled={disabled}
      onClick={onItemClick}
      className={cn(
        itemVariants({
          draggableItem: 'default',
          className,
        }),
      )}
      {...props}
    >
      <Draggable
        {...draggableIconProps}
        aria-disabled={disabled}
        data-align={draggableIconPosition}
        data-dragged={isDragged}
        className="shrink-0 cursor-grab fill-icon-secondary focus:outline-none focus-visible:outline-none active:cursor-grabbing aria-disabled:cursor-default aria-disabled:fill-text-disabled data-[align=left]:order-first data-[align=none]:hidden data-[align=right]:order-last data-[dragged=true]:cursor-grabbing"
      />
      {checkbox && (
        <Checkbox {...checkboxProps} disabled={disabled} onClick={onSelect} />
      )}

      <div
        data-align={adornmentPosition}
        className="flex min-w-0 flex-1 items-center gap-2 data-[align=right]:justify-between"
      >
        {adornment && (
          <span
            data-align={adornmentPosition}
            className="shrink-0 data-[align=left]:order-first data-[align=right]:order-last"
          >
            {adornment}
          </span>
        )}
        <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
          <TruncateText>
            <span
              className={typographyVariants({
                variant: 'bodyShort02',
                className: 'block w-full min-w-0 text-left',
              })}
            >
              {title}
            </span>
          </TruncateText>
          {description && (
            <TruncateText>
              <span
                className={cn(
                  typographyVariants({
                    variant: 'caption2',
                    color: 'tertiary',
                    className: 'block w-full min-w-0 text-left',
                  }),
                )}
              >
                {description}
              </span>
            </TruncateText>
          )}
        </div>
      </div>
    </button>
  );
};

export default DraggableItem;
